"use client";

import { useCallback, useMemo } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/axios";
import { DEFAULT_MAX_RETAINED_PAGES, nextPageWithinBound } from "./infinite-grid-bound";
type FetcherParams = {
  page: number;
  perPage: number;
  lang?: string;
  [key: string]: unknown;
};
type UseInfiniteGridOptions<T> = {
  /** The server-rendered first page. Seed it ONLY when it matches this key. */
  initialData?: any;
  /**
   * Backdate the seeded page so an authenticated viewer refetches immediately.
   * Pass `0` when `initialData` came from a cookie-free public fetch and the
   * payload carries `current_user` flags. Same rule as `useCollections`.
   */
  initialDataUpdatedAt?: number;
  apiEndpoint?: string;
  fetcher?: (params: FetcherParams) => Promise<any>;
  lang: string;
  pageSize?: number;
  queryParams?: Record<string, unknown>;
  enabled?: boolean;
  /**
   * The page this list ENTERS at, for a `?page=` deep link. Defaults to 1.
   *
   * It is the `initialPageParam`, not a target to walk towards: entering at page
   * 3 fetches page 3 and nothing else, so a deep link costs ONE request no
   * matter how deep it is. Walking 1→2→3 to "restore" the link would issue a
   * request per page and grow linearly with the number in the URL, which is the
   * unbounded-request shape this deliberately avoids.
   *
   * It is part of the query key, so two different entry points are two
   * different cached lists rather than one list that silently reinterprets its
   * own first page.
   */
  initialPage?: number;
  /**
   * THE HARD RETENTION BOUND: the maximum number of server pages this list will
   * ever hold at once. Defaults to `DEFAULT_MAX_RETAINED_PAGES`.
   *
   * WHY A NUMBER AND NOT "THE BUTTON". An earlier version of this hook recorded
   * that "the button IS the bound: nothing accumulates without an explicit user
   * action" (D055). That is a statement about INTENT, not a bound — a user can
   * press Load More arbitrarily many times, so the retained set had no ceiling
   * and the DOM could still grow without limit. Three authoritative statements
   * require an actual ceiling: `SINGLE_COLLECTION_REDESIGN.md` §36 ("the
   * mitigation specified is a BOUNDED ACCUMULATION with a 'back to top' and a
   * `?page=` deep-link entry"), §44 ("Accumulated DOM | Bounded, with a
   * measurement task before any virtualisation decision"), and both phase
   * briefs' "must not accumulate unbounded DOM".
   *
   * Once the bound is reached `getNextPageParam` returns `undefined`, so
   * `hasNextPage` is false and Load More cannot append a further page. The caller
   * offers the `?page=` continuation instead, which re-enters at the next page as
   * a FRESH window — which is why §36 names bounded accumulation, back-to-top and
   * the deep link as one mitigation rather than three.
   *
   * Windowing/virtualisation is deliberately NOT used: §36 and §44 both forbid
   * specifying it before a measurement task, and a page cap satisfies the
   * contract without it.
   */
  maxPages?: number;
};

/**
 * The cursor/infinite list pattern — REBUILT ON `useInfiniteQuery` in Phase 04.
 *
 * WHAT WAS WRONG (audit F41). This hook held the accumulated list in
 * `useState`, seeded it from `initialData`, tracked `page`/`hasMore` locally and
 * used `useQuery` as a fetch *trigger*. The consequence was that the list the
 * user was looking at lived OUTSIDE the React Query cache: it was not shared
 * between components, could not be invalidated by a mutation, and was thrown
 * away on unmount — so navigating away from a 200-item grid and back re-fetched
 * page 1 and silently discarded the other thirteen pages. It also bypassed the
 * documented query convention every other list in the product follows.
 *
 * WHAT IS PRESERVED VERBATIM:
 *   - the return shape, so no consumer had to change to get the fix;
 *   - the by-id de-duplication, which exists because some endpoints' filtered
 *     pagination has been observed to repeat an item across pages (a repeat
 *     rendered a second card and tripped React's duplicate-key check);
 *   - `hasMore` derived from `page.current_page < page.last_page` — the server's
 *     own answer, never "the last response was non-empty".
 *
 * WHAT CHANGED BESIDES THE CACHE: `error`/`isError`/`refetch` are exposed, so a
 * failed page can render an error with a working retry instead of reading as the
 * end of the list. Consumers drive `loadMore` from a real button rather than an
 * intersection observer alone (WCAG 2.1.1 and 2.4.7): the click is also what
 * bounds DOM growth, since nothing accumulates without an explicit user action.
 */
export function useInfiniteGrid<T>({
  initialData,
  initialDataUpdatedAt,
  apiEndpoint,
  fetcher,
  lang,
  pageSize = 15,
  queryParams = {},
  enabled = true,
  initialPage = 1,
  maxPages = DEFAULT_MAX_RETAINED_PAGES
}: UseInfiniteGridOptions<T>) {
  const entryPage = Number.isFinite(initialPage) && initialPage > 0 ? Math.floor(initialPage) : 1;
  const pageCap = Number.isFinite(maxPages) && maxPages >= 1 ? Math.floor(maxPages) : DEFAULT_MAX_RETAINED_PAGES;
  // Serialised, so an inline object literal at the call site does not change
  // the key on every render.
  const paramsKey = JSON.stringify(queryParams);
  const fetchPage = useCallback(async (page: number) => {
    if (fetcher) return fetcher({
      page,
      perPage: pageSize,
      lang,
      ...queryParams
    });
    if (apiEndpoint) {
      const search = new URLSearchParams();
      search.set("page", String(page));
      search.set("per_page", String(pageSize));
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "" && value !== false) {
          search.set(key, String(value));
        }
      });
      const separator = apiEndpoint.includes("?") ? "&" : "?";
      const {
        data
      } = await axiosInstance.get(`${apiEndpoint}${separator}${search.toString()}`);
      return data;
    }
    throw new Error("useInfiniteGrid: no fetcher and no apiEndpoint");
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [fetcher, apiEndpoint, pageSize, lang, paramsKey]);
  const queryClient = useQueryClient();
  const queryKey = ["infinite-grid", apiEndpoint ?? "custom", lang, pageSize, paramsKey, entryPage];
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({
      pageParam
    }) => fetchPage(pageParam as number),
    initialPageParam: entryPage,
    getNextPageParam: (lastPage: any, allPages: any[]) => nextPageWithinBound(lastPage, allPages.length, pageCap),
    initialData: initialData ? {
      pages: [initialData],
      pageParams: [1]
    } : undefined,
    initialDataUpdatedAt: initialData ? initialDataUpdatedAt : undefined,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    enabled
  });
  const items = useMemo(() => {
    const flat: T[] = (query.data?.pages ?? []).flatMap((page: any) => page?.data?.items ?? []);
    const seen = new Set<unknown>();
    return flat.filter((item: any) => {
      const id = item?.id;
      if (id === undefined || id === null) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [query.data]);
  const total: number | null = useMemo(() => {
    const pages = query.data?.pages;
    const meta = pages?.[pages.length - 1]?.data?.page;
    return typeof meta?.total === "number" ? meta.total : null;
  }, [query.data]);
  const loadMore = useCallback(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage();
  }, [query]);

  /**
   * THE CONTINUATION, for when the bound is reached and the server still has
   * pages. `hasMore` is false there — Load More genuinely cannot append — so the
   * caller offers a `?page=` re-entry instead, which starts a fresh window and
   * releases the retained one. Null when the bound is not the reason paging
   * stopped, i.e. when the list simply ended.
   */
  const retained = query.data?.pages?.length ?? 0;
  const nextEntryPage: number | null = useMemo(() => {
    if (retained < pageCap) return null;
    const params = query.data?.pageParams ?? [];
    const lastParam = params[params.length - 1];
    const lastMeta = query.data?.pages?.[retained - 1]?.data?.page;
    const nextPage = typeof lastParam === "number" ? lastParam + 1 : null;
    if (nextPage == null) return null;
    // Only offer it when the SERVER says there is something there.
    const lastPage = lastMeta?.last_page;
    if (typeof lastPage === "number" && nextPage > lastPage) return null;
    return nextPage;
  }, [query.data, retained, pageCap]);
  return {
    items,
    total,
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    hasMore: Boolean(query.hasNextPage),
    loadMore,
    /** Pages currently retained, and the hard ceiling on that number. */
    retainedPages: retained,
    maxRetainedPages: pageCap,
    /** True once the retention bound — not the end of the data — stopped paging. */
    atRetentionCap: retained >= pageCap,
    nextEntryPage,
    error: query.error,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
    /**
     * The retry the error states call. `resetQueries`, not `refetch`: a
     * `refetch()` on the state a failed browse request leaves behind was
     * measured to issue no request at all (D049 recorded the same on Home).
     */
    reset: () => queryClient.resetQueries({
      queryKey
    })
  };
}